import React, { useState } from "react";
import {
  Col,
  Row,
  ModalBody,
  Label,
  Input,
  Modal,
  ModalHeader,
  Form,
  ModalFooter,
  Button,
  FormFeedback,
} from "reactstrap";
import { useFormik, FormikHelpers } from "formik";
import * as Yup from "yup";

import * as Url from "../../../api/url_helper";
import { APIClient } from "../../../api/api_helper";
import {
  GetUserData,
  showToastMessage,
  handleChange,
} from "../../service/CommonService";

const api = new APIClient();

/* ---------- Types ---------- */

interface VerifyReasonSubmitPayload {
  id: number | string;
  reason: string;
  flag: "R" | "V" | null;
}

interface VerifyReasonProps {
  isOpen: boolean;
  toggle: () => void;
  action?: string;
  id: number | string;
  click: (payload: VerifyReasonSubmitPayload) => void;
  isRejectRequired?: boolean;
  isCancelRequired?: boolean;
}

interface VerifyReasonFormValues {
  password: string;
  reason: string;
  check: boolean;
}

/* ---------- Component ---------- */

const VerifyReason: React.FC<VerifyReasonProps> = ({
  isOpen,
  toggle,
  action,
  id,
  click,
  isRejectRequired = true,
  isCancelRequired = true,
}) => {
  const [flag, setFlag] = useState<"R" | "V" | null>(null);
  const [passwordShow, setPasswordShow] = useState<boolean>(false);

  const formik = useFormik<VerifyReasonFormValues>({
    initialValues: {
      password: "",
      reason: "",
      check: false,
    },
    validationSchema: Yup.object({
      password: Yup.string().required("Password is required."),
      reason: Yup.string().required("Reason is required."),
      check: Yup.boolean().oneOf([true], "Please confirm checked."),
    }),
    onSubmit: async (values) => {
      const user = GetUserData();

      if (!user) {
        showToastMessage("error", "User session expired. Please login again.");
        return;
      }

      const authObj = {
        userid: user.userid,
        
        
        secondarypassword: values.password,
      };

      api
        .create(
          Url.Admin_API.User_API + "UserSecondaryPassValidate",
          authObj
        )
        .then((response: any) => {
          if (response?.data?.isvalid) {
            const payload: VerifyReasonSubmitPayload = {
              id,
              reason: values.reason.trim(),
              flag,
            };
            toggle();
            click(payload);
          } else {
            showToastMessage("error", response?.message);
          }
        })
        .catch((error: unknown) => {
          showToastMessage("error", String(error));
        });
    },

  });

  return (
    <Modal
      id="showModal"
      isOpen={isOpen}
      toggle={toggle}
      top
      size="lg"
      backdrop="static"
      onClosed={() => formik.resetForm()}
    >
      <ModalHeader className="p-3 border-bottom" toggle={toggle}>
        User Verification
      </ModalHeader>

      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <Row className="g-3">
            <Col lg={12}>
              <Label className="form-label" htmlFor="password-input">
                Password<span className="text-danger">*</span>
              </Label>

              <div className="position-relative auth-pass-inputgroup">
                <Input
                  name="password"
                  value={formik.values.password}
                  type={passwordShow ? "text" : "password"}
                  className="form-control pe-5"
                  placeholder="Enter Password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    !!(formik.touched.password && formik.errors.password)
                  }
                />

                {formik.touched.password && formik.errors.password && (
                  <FormFeedback>{formik.errors.password}</FormFeedback>
                )}

                <button
                  type="button"
                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                  onClick={() => setPasswordShow((prev) => !prev)}
                >
                  <i className="mdi mdi-eye-off-outline fs-18" />
                </button>
              </div>
            </Col>

            <Col lg={12}>
              <Label htmlFor="reason" className="form-label">
                Reason<span className="text-danger">*</span>
              </Label>

              <Input
                type="textarea"
                id="reason"
                name="reason"
                value={formik.values.reason}
                onChange={(e) => handleChange(formik, e)}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.reason && formik.errors.reason)}
                placeholder="Enter reason..."
              />

              {formik.touched.reason && formik.errors.reason && (
                <FormFeedback>{formik.errors.reason}</FormFeedback>
              )}
            </Col>

            <Col lg={12}>
              <div className="form-check">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  name="check"
                  checked={formik.values.check}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.check && formik.errors.check)}
                  id="check"
                />
                <Label htmlFor="check" className="form-check-label">
                  I confirm that, to the best of my knowledge, the description
                  provided accurately explains the reason for the change.
                </Label>

                {formik.touched.check && formik.errors.check && (
                  <FormFeedback>{formik.errors.check}</FormFeedback>
                )}
              </div>
            </Col>
          </Row>
        </ModalBody>

        <ModalFooter className="border-top pt-2 pb-2">
          <div className="hstack gap-2 justify-content-end">
            {isCancelRequired && (
              <Button color="outline" onClick={toggle}>
                Cancel
              </Button>
            )}

            {isRejectRequired && (
              <Button
                type="submit"
                color="danger"
                onClick={() => setFlag("R")}
              >
                Reject
              </Button>
            )}

            <Button
              type="submit"
              color="primary"
              onClick={() => setFlag("V")}
            >
              {action || "Approve"}
            </Button>
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default VerifyReason;
